/**
 * Hybrid-Provider — Smart-Routing pro Symbol-Format.
 *
 * - Krypto-Pairs (Slash im Symbol oder Coin-Suffix) → komplett TwelveData
 *   (Finnhub Free liefert für BTC/USD-Format kein Quote → no_data).
 *   W73.7 — CoinGecko-Fallback wenn TwelveData null/stale: liest
 *   `v5_crypto_fundamentals.current_price` (refresh-coingecko-cron alle 30 Min).
 *   Audit Agent F HIGH: BTC zeigte "Kurs 8 Std alt", ETH "Kurs nie" — in
 *   24/7-Krypto-Markt sind 8h alte Daten tot. CoinGecko ist konsistenter.
 * - Alles andere → Finnhub für Quote/Profile (ROE, KGV, MarketCap),
 *   TwelveData für OHLCV.
 *
 * Lookup geht über Finnhub für Aktien/ETFs (mehr Treffer), für reine Crypto-
 * Suchen via TwelveData.
 */

import { FINNHUB_PROVIDER } from './finnhub-provider';
import { TWELVEDATA_PROVIDER } from './twelvedata-provider';
import { fetchCryptoFundamentals } from '../crypto-fundamentals';
import { DataStateHelpers } from '@/types';
import type { DataState } from '@/types';
import type { MarketDataProvider, PriceQuote } from './types';

const KNOWN_CRYPTO_BASES = new Set([
  'BTC', 'ETH', 'SOL', 'XRP', 'BNB', 'ADA', 'AVAX', 'DOT',
  'LINK', 'ATOM', 'MATIC', 'LTC', 'DOGE', 'UNI', 'TRX', 'NEAR',
]);

function isCryptoSymbol(symbol: string): boolean {
  const upper = symbol.toUpperCase().trim();
  // BTC/USD, ETH-USD, BTCUSDT etc.
  if (upper.includes('/') || upper.endsWith('USDT')) return true;
  // BTC-USD, ETH-USD (Yahoo-Format)
  const dashSplit = upper.split('-');
  if (dashSplit.length === 2 && KNOWN_CRYPTO_BASES.has(dashSplit[0] ?? '')) return true;
  return false;
}

function isCryptoQuery(query: string): boolean {
  const upper = query.toUpperCase().trim();
  // Falls die Suche bereits ein Krypto-Pair-Format hat
  if (upper.includes('/')) return true;
  // Falls die Suche nur eine Krypto-Base ist (BTC, ETH, …)
  if (KNOWN_CRYPTO_BASES.has(upper)) return true;
  return false;
}

/**
 * W73.7 — CoinGecko-Fallback fuer Krypto-Quotes wenn TwelveData scheitert
 * oder veraltete Daten liefert. Liest v5_crypto_fundamentals (alle 30 Min
 * via refresh-coingecko-cron). Symbol wird auf Base-Coin reduziert: BTC/USD
 * → BTC, BTC-USD → BTC.
 */
async function getCryptoQuoteFromCoinGecko(
  symbol: string,
  signal?: AbortSignal
): Promise<DataState<PriceQuote>> {
  const upper = symbol.toUpperCase().trim();
  // W73.7 Fix: v5_crypto_fundamentals.symbol hat das Pair-Format (BTC/USD, ETH/USD).
  // Versuche das Original-Symbol zuerst, dann Pair-Varianten als Fallback.
  const lookupCandidates: string[] = [upper];
  // BTC → BTC/USD
  if (!upper.includes('/') && !upper.includes('-') && !upper.endsWith('USDT')) {
    lookupCandidates.push(`${upper}/USD`);
  }
  // BTC-USD → BTC/USD
  if (upper.includes('-')) {
    const base = upper.split('-')[0] ?? upper;
    lookupCandidates.push(`${base}/USD`);
  }
  // BTCUSDT → BTC/USD
  if (upper.endsWith('USDT')) {
    const base = upper.slice(0, -4);
    lookupCandidates.push(`${base}/USD`);
  }

  const ac = signal ?? new AbortController().signal;
  try {
    let fund = null;
    for (const candidate of lookupCandidates) {
      fund = await fetchCryptoFundamentals(candidate, ac);
      if (fund && fund.current_price !== null && fund.current_price > 0) break;
    }
    if (!fund || fund.current_price === null || fund.current_price <= 0) {
      return DataStateHelpers.noData();
    }
    const price = fund.current_price;
    const ath = fund.ath_usd ?? 0;
    const atl = fund.atl_usd ?? 0;
    const ddFromAth = ath > 0 && price > 0 ? ((price - ath) / ath) * 100 : null;
    const quote: PriceQuote = {
      symbol,
      current_price: price,
      change_pct_today: 0,
      high_52w: ath,
      low_52w: atl,
      market_cap_billions: fund.market_cap_usd !== null ? fund.market_cap_usd / 1e9 : null,
      pe_ttm: null,
      fetched_at: fund.fetched_at,
      return_5d_pct: null,
      return_13w_pct: null,
      return_26w_pct: null,
      return_52w_pct: null,
      roe_ttm_pct: null,
      drawdown_from_52w_high_pct: ddFromAth,
      industry: null,
    };
    return DataStateHelpers.success(quote, 300);
  } catch (err) {
    return DataStateHelpers.error({
      code: 'coingecko_fallback_failed',
      message: err instanceof Error ? err.message : String(err),
      retry_possible: true,
    });
  }
}

/**
 * W73.7 — TwelveData-Quote mit CoinGecko-Fallback fuer Krypto.
 * Fallback triggert wenn: status != success ODER fetched_at > 2h alt
 * (Krypto-Markt ist 24/7, 2h ist Schwelle wo TwelveData definitiv kaputt ist).
 */
async function getCryptoQuoteWithFallback(
  symbol: string,
  signal?: AbortSignal
): Promise<DataState<PriceQuote>> {
  const STALE_HOURS = 2;
  const twelveData = await TWELVEDATA_PROVIDER.getQuote(symbol, signal);
  // Falls TwelveData fehlschlug → CoinGecko probieren
  if (twelveData.status !== 'success' || twelveData.data === null) {
    return getCryptoQuoteFromCoinGecko(symbol, signal);
  }
  // Falls TwelveData-Daten zu alt (>2h) → CoinGecko nehmen wenn aktueller
  const fetchedAt = new Date(twelveData.data.fetched_at).getTime();
  const ageHours = (Date.now() - fetchedAt) / 3600000;
  if (ageHours > STALE_HOURS) {
    const cg = await getCryptoQuoteFromCoinGecko(symbol, signal);
    if (cg.status === 'success' && cg.data !== null) {
      const cgAge = (Date.now() - new Date(cg.data.fetched_at).getTime()) / 3600000;
      if (cgAge < ageHours) return cg; // CoinGecko ist frischer
    }
  }
  return twelveData;
}

/**
 * W79.3 — CoinGecko-OHLCV-Fallback fuer Krypto-Symbole (Audit-2 Agent F HIGH).
 *
 * TwelveData liefert oft "kein OHLCV — Provider liefert keine Bars" fuer
 * Altcoins (SOL/XRP/BNB/ADA/DOGE/AVAX/DOT/LINK). CoinGecko `/coins/{id}/ohlc`
 * ist gratis (max 30 calls/min) und liefert 4h-Candles fuer die letzten 1/7/
 * 14/30/90/180/365 Tage.
 *
 * Fuer 250 Daily-Bars (~1 Jahr) → days=365 abrufen, OHLC-Bars (4h-Aufloesung
 * = ~6 Bars/Tag, ~2190 Bars/Jahr) auf Daily aggregieren.
 */
const SYMBOL_TO_COINGECKO_ID: Record<string, string> = {
  'BTC/USD': 'bitcoin',
  'ETH/USD': 'ethereum',
  'SOL/USD': 'solana',
  'XRP/USD': 'ripple',
  'ADA/USD': 'cardano',
  'BNB/USD': 'binancecoin',
  'DOGE/USD': 'dogecoin',
  'AVAX/USD': 'avalanche-2',
  'DOT/USD': 'polkadot',
  'LINK/USD': 'chainlink',
};

async function getCryptoOhlcvFromCoinGecko(
  symbol: string,
  bars: number,
  signal?: AbortSignal
): Promise<DataState<import('./types').OhlcvBar[]>> {
  const upper = symbol.toUpperCase().trim();
  const cgId = SYMBOL_TO_COINGECKO_ID[upper];
  if (!cgId) return DataStateHelpers.noData();
  // days-Parameter: 365 fuer ein Jahr, sonst 90/180 je nach bars
  const days = bars >= 250 ? 365 : bars >= 100 ? 180 : 90;
  try {
    // W82 — Zwei parallele Endpunkte:
    // 1. /ohlc → 4h-Bars mit OHLC, KEIN Volume
    // 2. /market_chart → daily/hourly prices + total_volumes
    // Wir aggregieren OHLC auf Daily und mergen pro Tag das Volume hinein.
    const ohlcUrl = `https://api.coingecko.com/api/v3/coins/${cgId}/ohlc?vs_currency=usd&days=${days}`;
    const mcUrl = `https://api.coingecko.com/api/v3/coins/${cgId}/market_chart?vs_currency=usd&days=${days}&interval=daily`;
    const [ohlcRes, mcRes] = await Promise.all([
      fetch(ohlcUrl, { signal }),
      fetch(mcUrl, { signal }),
    ]);
    if (!ohlcRes.ok) return DataStateHelpers.noData();
    const ohlcJson = (await ohlcRes.json()) as Array<[number, number, number, number, number]>;
    if (!Array.isArray(ohlcJson) || ohlcJson.length === 0) return DataStateHelpers.noData();

    // W82-B — Volume-Map aus market_chart (LETZTER value pro Tag = final daily volume).
    // total_volumes ist 24h-Rolling, LATEST des Tages = "Total Daily Volume".
    const volumeByDay = new Map<string, number>();
    if (mcRes.ok) {
      try {
        const mcJson = (await mcRes.json()) as {
          total_volumes?: Array<[number, number]>;
        };
        if (mcJson?.total_volumes) {
          for (const [ts, vol] of mcJson.total_volumes) {
            const day = new Date(ts).toISOString().slice(0, 10);
            volumeByDay.set(day, vol); // letzter Punkt eines Tages ueberschreibt
          }
        }
      } catch {
        // Volume-Parse-Fehler → Volume bleibt 0, OHLC nutzbar
      }
    }

    // CoinGecko ohlc-Endpoint liefert 4h-Bars. Wir aggregieren auf Daily
    // (6 4h-Bars pro Tag, first open + max high + min low + last close).
    const byDay = new Map<string, { open: number; high: number; low: number; close: number }>();
    for (const row of ohlcJson) {
      const [ts, o, h, l, c] = row;
      const day = new Date(ts).toISOString().slice(0, 10);
      const existing = byDay.get(day);
      if (!existing) {
        byDay.set(day, { open: o, high: h, low: l, close: c });
      } else {
        existing.high = Math.max(existing.high, h);
        existing.low = Math.min(existing.low, l);
        existing.close = c; // letzter Bar des Tages
      }
    }
    const dailyBars: import('./types').OhlcvBar[] = Array.from(byDay.entries())
      .map(([day, ohlc]) => ({
        timestamp: `${day}T00:00:00.000Z`,
        open: ohlc.open,
        high: ohlc.high,
        low: ohlc.low,
        close: ohlc.close,
        volume: volumeByDay.get(day) ?? 0,
      }))
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
      .slice(-bars);
    if (dailyBars.length === 0) return DataStateHelpers.noData();
    return DataStateHelpers.success(dailyBars, 30 * 60);
  } catch {
    return DataStateHelpers.noData();
  }
}

export const HYBRID_PROVIDER: MarketDataProvider = {
  name: 'Hybrid (Finnhub + TwelveData + CoinGecko-Fallback)',
  is_live: true,

  lookupSymbol(query, signal) {
    if (isCryptoQuery(query)) {
      return TWELVEDATA_PROVIDER.lookupSymbol(query, signal);
    }
    return FINNHUB_PROVIDER.lookupSymbol(query, signal);
  },

  getQuote(symbol, signal) {
    if (isCryptoSymbol(symbol)) {
      // W73.7 — mit CoinGecko-Fallback statt nur TwelveData
      return getCryptoQuoteWithFallback(symbol, signal);
    }
    return FINNHUB_PROVIDER.getQuote(symbol, signal);
  },

  async getOhlcv(symbol, timeframe, bars, signal) {
    // W79.3 — fuer Krypto-Symbole: TwelveData zuerst, dann CoinGecko-Fallback
    // wenn TwelveData "no_data" liefert. Audit-2 Agent F HIGH: 9/10 Alts
    // hatten "kein OHLCV — Provider liefert keine Bars".
    // W82-A — Zusaetzlich Staleness-Check: TwelveData liefert oft VERALTETE
    // Bars (z.B. SOL bis 03.05 stehen geblieben, 22 Tage alt). Krypto-Markt
    // ist 24/7 — neueste Daily-Bar darf nicht > 30h alt sein.
    if (isCryptoSymbol(symbol)) {
      const td = await TWELVEDATA_PROVIDER.getOhlcv(symbol, timeframe, bars, signal);
      if (td.status === 'success' && td.data && td.data.length > 0) {
        const latestBarTs = td.data[td.data.length - 1]?.timestamp ?? null;
        const latestBarAgeH = latestBarTs
          ? (Date.now() - new Date(latestBarTs).getTime()) / 3600000
          : Infinity;
        const STALE_HOURS = 30; // 24h-Krypto-Tag + 6h Toleranz fuer Daily-Close-Lag
        if (latestBarAgeH < STALE_HOURS) {
          return td;
        }
        // Fallthrough → CoinGecko probieren falls Daten frischer
        if (timeframe === 'daily') {
          const cg = await getCryptoOhlcvFromCoinGecko(symbol, bars, signal);
          if (cg.status === 'success' && cg.data && cg.data.length > 0) {
            const cgLatest = cg.data[cg.data.length - 1]?.timestamp ?? null;
            const cgAgeH = cgLatest
              ? (Date.now() - new Date(cgLatest).getTime()) / 3600000
              : Infinity;
            // CoinGecko-Daten nehmen wenn frischer als TwelveData
            if (cgAgeH < latestBarAgeH) return cg;
          }
        }
        // Beide stale → trotzdem TwelveData (besser als nichts)
        return td;
      }
      // TwelveData no_data → CoinGecko-Fallback (nur fuer daily)
      if (timeframe === 'daily') {
        return getCryptoOhlcvFromCoinGecko(symbol, bars, signal);
      }
      return td;
    }
    return TWELVEDATA_PROVIDER.getOhlcv(symbol, timeframe, bars, signal);
  },
};
